import React, {
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useState,
} from 'react';
import PricingContext from '../../../context/pricing';
import { setPackage } from '../../../actions/pricing';
import Theme from '../../Theme';
import {
  StyledPlanBox,
  StyledPlanBoxHeader,
  StyledPlanContent,
  StyledPlanList,
} from './styles';
import CheckIcon from '../../SVG/CheckIcon';
import XIcon from '../../SVG/XIcon';
import Tooltip from '../Tooltip';
import { SIGNUP_URL } from '../../../constants/pricing/config';

const PlanBox = ({ category, plan, features, children }) => {
  const { state, dispatch } = useContext(PricingContext);

  const planName = plan.name.toLowerCase();

  const selectedVolume =
    planName in state.volume[category] ? state.volume[category][planName] : 0;

  const selectedContacts =
    planName in state.contacts ? state.contacts[planName] : 0;

  const selectedPackage = useMemo(() => {
    const packages = Object.values(plan.packages);

    if (packages.length === 1) return packages[0];
    return packages.find(
      (pkg) =>
        (!pkg.volumeBreakpoint &&
          selectedVolume <= pkg.volume &&
          (!pkg.contacts || selectedContacts <= pkg.contacts)) ||
        (pkg.volumeBreakpoint &&
          selectedVolume < pkg.volumeBreakpoint &&
          (!pkg.contactsBreakpoint ||
            selectedContacts < pkg.contactsBreakpoint))
    );
  }, [selectedVolume, selectedContacts, plan]);

  const isInCart = useMemo(
    () =>
      Object.values(plan.packages).some(
        (pkg) => pkg.id === state.packages[category]
      ),
    [state.packages.mc, state.packages.e]
  );

  const isCustom = useMemo(
    () =>
      ((planName === 'pro' || planName === 'advanced') &&
        Object.values(plan.packages).pop().volume === selectedVolume) ||
      (planName === 'advanced' &&
        Object.values(plan.packages).pop().contacts === selectedContacts),
    [planName, selectedVolume, selectedContacts]
  );

  const { colorBlue } = Theme.color;
  const { colorGreen } = Theme.color;
  const { colorPinkDark } = Theme.color;
  const { scaleDown03 } = Theme.scale;

  const printFeature = useCallback(
    (feature) => {
      const isExcluded =
        'exclude' in feature && feature.exclude.includes(planName);

      const text =
        typeof feature.title === 'object'
          ? feature.title[planName]
          : feature.title;

      const tooltipText =
        feature.tooltip && typeof feature.tooltip === 'object'
          ? feature.tooltip[planName]
          : feature.tooltip;

      const iconClasses = ['icon-wrapper'];
      if (isExcluded) iconClasses.push('excluded');

      return (
        <li key={text}>
          <div className={iconClasses.join(' ')}>
            {isExcluded ? (
              <XIcon size={scaleDown03} color={colorPinkDark} />
            ) : (
              <CheckIcon size={scaleDown03} color={colorGreen} />
            )}
          </div>
          <span>
            {tooltipText ? <Tooltip tip={tooltipText}>{text}</Tooltip> : text}
          </span>
        </li>
      );
    },
    [features, plan]
  );

  const printCta = useCallback(() => {
    if (isCustom || !selectedPackage) {
      return (
        <a
          href="/contact-us-form/"
          className="btn btn--primary btn--full-width m-top-1"
          data-testid={`cta-${planName}`}
        >
          Talk to an Expert
        </a>
      );
    }
    // if (isInCart) {
    //   return (
    //     <button
    //       type="button"
    //       className="btn btn--secondary btn--full-width m-top-1"
    //       data-testid={`cta-${planName}`}
    //       onClick={() => resetPackage(category, dispatch)}
    //     >
    //       Remove from Cart
    //     </button>
    //   );
    // }
    return (
      <button
        type="button"
        className="btn btn--primary btn--full-width m-top-1"
        data-testid={`cta-${planName}`}
        onClick={() => {
          setPackage(category, selectedPackage.id, dispatch);
          window.location = SIGNUP_URL;
        }}
        // disabled={!isInCart && state.packages[category] !== null}
      >
        Start for free
      </button>
    );
  }, [state.packages.e, state.packages.mc, isInCart, isCustom]);

  const printFootnote = useCallback(() => {
    if (plan.packages) {
      if (planName !== 'free')
        return <p className="footnote">* Taxes and overages may apply.</p>;
      return <p className="footnote">No credit card, no commitment.</p>;
    }
    return null;
  }, [plan]);

  const printEstimate = useCallback(() => {
    if (isCustom) {
      return '⚡️Customized Offer⚡️';
    }
    if ((selectedVolume || selectedContacts) && selectedPackage) {
      return `$${selectedPackage.rate.toLocaleString()}/mo estimated*`;
    }
    if (typeof plan.startsAt === 'string') {
      return plan.startsAt;
    }
    return `Starts at $${plan.startsAt.toLocaleString()}/mo*`;
  }, [selectedPackage, selectedVolume, selectedContacts, isCustom]);

  const printHeader = useCallback(() => {
    if (isCustom) {
      return 'Custom Pricing';
    }
    if (selectedPackage && (selectedVolume > 0 || selectedContacts > 0)) {
      return selectedPackage.name;
    }
    return plan.name;
  }, [selectedVolume, selectedContacts, selectedPackage, isCustom]);

  const printSubHeader = useCallback(() => {
    if (isCustom) {
      return 'Need to send more? Talk to us for custom, high volume pricing.';
    }
    if (selectedPackage && (selectedVolume > 0 || selectedContacts > 0)) {
      return (
        <>
          {'Send up to '}
          {'contacts' in selectedPackage &&
            `${selectedPackage.contacts.toLocaleString()} contacts and `}
          {`${selectedPackage.volume.toLocaleString()} emails/mo `}
          {'before '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://sendgrid.com/docs/ui/account-and-settings/billing/#limits-and-overage-rates"
          >
            overages apply
          </a>
          .
        </>
      );
    }
    return plan.subHead;
  }, [selectedVolume, selectedContacts, selectedPackage, isCustom]);

  const [isFeatureListOpen, setFeatureListOpen] = useState(false);
  const toggleFeatureList = () => setFeatureListOpen(!isFeatureListOpen);

  const printShowFeatures = () => (
    <button
      type="button"
      className={`plan-box-feature-hider ${
        isFeatureListOpen ? '' : 'is-active'
      }`}
      onClick={toggleFeatureList}
    >
      <div className="icon-wrapper">
        <XIcon size={scaleDown03} color={colorBlue} />
      </div>
      Show Features
    </button>
  );

  useEffect(() => {
    if (!isInCart || !selectedPackage) return;
    if (selectedPackage.id !== state.packages[category]) {
      setPackage(category, selectedPackage.id, dispatch);
    }
  });

  const elements = React.Children.toArray(children);

  return (
    <StyledPlanBox className={isCustom ? 'custom' : planName}>
      <StyledPlanBoxHeader>
        <h3 className="is-h2 m-bottom-0">{printHeader()}</h3>
        <p className="m-bottom-0">{printSubHeader()}</p>
      </StyledPlanBoxHeader>
      <StyledPlanContent>
        <h3 className="m-bottom-0">{printEstimate()}</h3>

        {elements.map((el) => React.cloneElement(el, { name: printHeader() }))}

        {printCta()}

        {printFootnote()}

        {printShowFeatures()}
      </StyledPlanContent>
      <StyledPlanList className={isFeatureListOpen ? 'is-open' : null}>
        {features.map(printFeature)}
      </StyledPlanList>
    </StyledPlanBox>
  );
};

export default PlanBox;
